import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { upstreamsApi } from '../api/upstreamsApi';
import type { UpstreamFilters, UpstreamFormValues } from '../types/upstream';

export function useUpstreamsQuery(
  filters: UpstreamFilters,
  page: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['gateway', 'upstreams', filters, page],
    queryFn: () => upstreamsApi.list({ ...filters, page, size: 20, sort: 'createdAt,desc' }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpstreamQuery(id: number | null) {
  return useQuery({
    queryKey: ['gateway', 'upstream', id],
    queryFn: () => upstreamsApi.get(id!),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

export function useCreateUpstream(onSuccess?: (name: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpstreamFormValues) => upstreamsApi.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'upstreams'] });
      onSuccess?.(created.name);
    },
  });
}

export function useReplaceUpstream(id: number, onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpstreamFormValues) => upstreamsApi.replace(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'upstreams'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'upstream', id] });
      onSuccess?.();
    },
  });
}

export function useDeleteUpstream(onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => upstreamsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'upstreams'] });
      onSuccess?.();
    },
  });
}
